/**
 * Egzersiz ekranı.
 *
 * Çizim modelinin ikinci tüketicisi: yazdırma oluşturucusu aynı düğümleri
 * A4'e basıyor, burası ekrana çiziyor ve etkileşimli hale getiriyor.
 *
 * Ama her egzersiz aynı şekilde cevaplanmıyor. Dört etkileşim biçimi var
 * ve hangisinin kullanılacağı şablona göre belirleniyor:
 *
 *   isaretle  hücrelere/seçeneklere dokunarak işaretle (varsayılan)
 *   yaz       kutulara yaz; şekil isteniyorsa klavye yerine palet
 *   damgala   paletten bir şekil seç, hücrelere bas
 *   cevir     kapalı kartları çevirerek eşleştir
 *
 * Bunları tek bir genel çizim döngüsüne sığdırmaya çalışmak, ekranda
 * cevaplanamayan egzersizler üretiyordu: şekil dizisini klavyeyle yazmak,
 * zaten açık duran kartları "çevirmek", hiç çalınmayan sesi dinlemek gibi.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { Serit } from '../components/Cerceve'
import { Dizi, Gozler, Uyaran, UyaranAdi, kimlik } from '../components/Sekil'
import {
  EXERCISE_CODES,
  EXERCISES,
  generateExercise,
  getExercise,
  type ExerciseCode,
  type ExerciseInstance,
  type GradeResult,
  type RenderNode,
} from '@nirengi/engine'

export type Mode =
  | 'isaretle'
  | 'yaz'
  | 'damgala'
  | 'cevir'
  | 'cizgi'
  | 'kelime'
  | 'dizi'
  | 'sihirbaz'
  /** iz sürme: duraklara sırayla dokun */
  | 'iz'
  /** nokta ızgarasına kopyala: noktalara dokunarak çiz */
  | 'kopyala'
  /** sahneye bak, kapanınca soruları cevapla */
  | 'bak'
  /** gizli şekil: desenin üzerinden geç */
  | 'gizli'
  /** Londra Kulesi: çubuktan çubuğa top taşı */
  | 'kule'
  /** koridor: parmakla iz sürme */
  | 'koridor'
  /** ritim: dinle, aynı ritimde dokun */
  | 'ritim'

/**
 * "Bunu ekranda nasıl yapacağım" satırı.
 *
 * Motorun ürettiği yönerge GÖREVİ anlatır ve kâğıtta da geçerlidir;
 * burada eklenen satır EKRANDA nasıl cevaplanacağını anlatır. İkisi
 * ayrı tutuluyor çünkü kâğıtta çocuk çiziyor, ekranda dokunuyor —
 * aynı cümle ikisine birden uymuyor.
 */
const EXERCISE_HINT: Record<ExerciseCode, string> = {
  scan_target:
    'Şekli gördüğün her kutuya dokun. Soldan sağa, satır satır ilerlersen hiçbirini kaçırmazsın.',
  spot_difference:
    'Soldaki tablo örnek, ona dokunamazsın. Sağdakinde farklı olan kutulara dokun.',
  odd_one_out:
    'Her satırda sadece bir tane farklı var. O satırı bitirmeden diğerine geçme.',
  word_search:
    'Kelimeyi bulunca önce ilk harfine, sonra son harfine dokun. Kelimeler düz, dik ya da çapraz durabilir.',
  path_trace:
    'Çizgiyi gözünle takip et; çizgiler birbirinin üstünden geçiyor, karıştırma. Sonra kutuya sayıyı yaz.',
  span_recall:
    'Önce "Diziyi göster" düğmesine bas. Dizi bitince aşağıdaki şekillere sırayla dokunarak cevabını yaz.',
  memory_pairs:
    'Her seferinde iki kart açabilirsin. Aynıysa açık kalır, değilse kapanır. Kapanan kartların yerini aklında tut.',
  symbol_code:
    'Yukarıdaki anahtarda hangi şeklin hangi sayı olduğu yazıyor. Sırayla ilerle, hiçbirini atlama.',
  listening_steps:
    'Önce "Dinle" düğmesine bas; yönerge bir kez söylenir. Sonra aşağıdan bir şekil seç ve kutuya dokun.',
  reading_target:
    'Metni baştan sona oku. Kelimeyi her gördüğünde say, sonra toplam sayıyı kutuya yaz.',
  matrix_pattern:
    'Satırlara ve sütunlara bak, şekillerin sırası bir kurala uyuyor. Kuralı bulursan boş kutuya ne geleceğini anlarsın.',
  sequence_complete:
    'Sayılar arasında ne kadar fark olduğuna bak. Aynı kural sonuna kadar devam ediyor.',
  logic_grid:
    'İpuçlarını tek tek oku. En kısa olanı 1. kutuya, en uzun olanı sonuncuya yaz.',
  sudoku_mini:
    'Önce aşağıdan bir sayı seç, sonra boş kutuya dokun. Koyu renkli kutular hazır verilmiş, onlar değişmez.',
  category_sort:
    'Kelimeler tek tek gelecek. Her kelime için aşağıdaki gruplardan birine dokun.',
  maze: "A'dan başla ve komşu kutulara dokunarak ilerle. Duvardan geçemezsin. Yanlış gittiysen bir önceki kutuya dokun, geri alır.",
  mental_rotation:
    'Şekli kafanda çevirmeye çalış. Aradığın, aynadaki hali değil; çevrilmiş hali.',
  mirror_image:
    'Sayfayı ortadan katlasan üst üste gelecek şekilde düşün. Sağ taraftaki kutulara dokun.',
  arith_fluency:
    'Sırayla ilerle. Takıldığın işlemi atla, sonra geri dön.',
  opposite_rule:
    'Ok yukarı bakıyorsa aşağı, sağa bakıyorsa sola yazacaksın. Cevapları aşağıdaki oklardan seçiyorsun.',
  dual_sequence:
    'Bir sayı atlayarak bak: 1., 3., 5. sayılar bir kurala, aradakiler başka bir kurala uyuyor.',
  arith_grid:
    'Önce aşağıdan bir sayı ya da işaret seç, sonra boş kutuya dokun. Satırın sonucu sağda, sütunun sonucu altta yazıyor.',
  trail_making:
    'Duraklara sırayla dokun. Yanlış dokunduysan son durağa tekrar dokun, geri alır.',
  word_ladder:
    'Yazdığın her kelime listede olacak ve bir öncekinden tek harfle ayrılacak. Sondan başa doğru da düşünebilirsin.',
  word_fluency: 'Bu çalışma kâğıt üzerinde yapılıyor.',
  dot_copy:
    'Sağdaki noktalara dokunarak çiz. Komşu olmayan noktaya atlayamazsın; son noktaya tekrar dokunmak geri alır.',
  delayed_recall:
    'Resme bakarken sayma: hangi şekilden kaç tane olduğunu aklında tut. Resim kapandıktan sonra soracağım.',
  overlap_find:
    'Çizgiler birbirinin içinden geçiyor. Bir şeklin tamamını değil, kenarlarını takip et.',
  hidden_figure:
    'Desendeki noktalara sırayla dokun. Sadece çizili olan çizgiler üzerinden gidebilirsin; son noktaya tekrar dokunmak geri alır.',
  tower_of_london:
    'Önce topu alacağın çubuğa, sonra koyacağın çubuğa dokun. Hamleyi yapmadan önce sonucunu düşün: geri almak da bir hamle sayılır.',
  rule_maze:
    'Yolu çizerken sayıları topla. En kısa yol toplamı aşıyor; daha uzun ama daha küçük sayılı bir yol var.',
  code_language:
    'Verilen kelimeleri karşılaştır: hangi hece hangi anlama geliyor? Cevabı aşağıdaki hecelere dokunarak kur.',
  contradiction:
    'Cümleleri tek tek oku ve her birini aklında tut. İki cümle aynı anda doğru olamaz; onları bul.',
  schedule_plan:
    'Önce bir işe dokun, sonra aşağıdan saatini seç. Randevunun saati sabit, onu değiştiremezsin.',
  route_plan:
    'Haritada hatları takip et. Rotayı durak adlarına sırayla dokunarak yaz; kapalı durak palette yok.',
  false_belief:
    'Hikâyeyi oku. Soru "sen nerede olduğunu biliyor musun" değil: o kişinin ne bildiğini düşün.',
  social_cue:
    'Söylenen sözle beden dili her zaman aynı şeyi anlatmaz. İkinci soruda, gösteren kişinin yalnızca sözü duyduğunu unutma.',
  alternative_uses: 'Bu çalışma kâğıt üzerinde yapılıyor.',
  incomplete_figures: 'Bu çalışma kâğıt üzerinde yapılıyor.',
  track_path:
    "Parmağını A'ya koy ve kaldırmadan B'ye götür. Parmağını kaldırırsan baştan başlarsın.",
  rhythm_tap:
    'Ritmi dinlerken kutu yanıp sönüyor; sesi kapalıysa da takip edebilirsin. Aynı hızda olman gerekmiyor, aralar aynı oranda olsun.',
  eye_emotion:
    'Yüzün geri kalanı yok, sadece gözler var. Üç şeye bak: kaşların eğimi, göz kapaklarının açıklığı, bakışın yönü.',
  zebra_grid:
    'Tek bir ipucu cevabı vermiyor. Önce kesin olanları yerleştir, sonra "olamaz" diyen ipuçlarıyla kalanları ele.',
  balance_scale:
    'Önce üstteki terazilerden her şeklin kaç tane ettiğini bul. Sonra soru terazisindeki sol kefenin toplamını hesapla.',
  overlay_layers:
    'Kâğıtlar saydam: hiçbir nokta kaybolmuyor. Bir kâğıtta olan nokta, sonuçta da var.',
  paper_fold:
    'Delik katlıyken açıldı, yani bütün katları birden deldi. Kâğıdı zihninde ters sırada aç; her açılışta delikler kat çizgisinin öbür yanına aynalanır.',
  flow_trace:
    'Kuralları sırayla değil, gönderildiğin gibi izle. Her adımda sayının ve şeklin son hâlini aklında tut.',
}

/**
 * DISARI ACIK: sinamalar hangi kipin secildigini dogruluyor.
 *
 * Uygulamanin disindan cagrilmiyor; disa acilmasinin tek sebebi
 * `test/ekran.ts`. Kip secimi sessizce kayabilen bir sey — yeni bir
 * sablon eklendiginde varsayilan dala dusup yazilamaz bir sayfa
 * verebiliyor.
 */
export function modeOf(code: ExerciseCode, instance: ExerciseInstance): Mode {
  if (code === 'rhythm_tap') return 'ritim'
  if (code === 'track_path') return 'koridor'
  if (code === 'tower_of_london') return 'kule'
  if (code === 'hidden_figure') return 'gizli'
  if (code === 'trail_making') return 'iz'
  if (code === 'dot_copy') return 'kopyala'
  if (code === 'delayed_recall') return 'bak'
  if (code === 'arith_grid') return 'damgala'
  if (code === 'memory_pairs') return 'cevir'
  if (code === 'span_recall') return 'dizi'
  if (code === 'category_sort') return 'sihirbaz'
  if (code === 'word_search') return 'kelime'
  if (code === 'maze' || code === 'rule_maze') return 'cizgi'
  if (code === 'listening_steps' || code === 'sudoku_mini') return 'damgala'
  const hasRows = instance.render.some((n) => n.t === 'rows' && n.items.some((i) => i.blank))
  return hasRows ? 'yaz' : 'isaretle'
}

/** Sunucudan gelen ödev tarifi */
export interface OdevTarifi {
  id: string
  kod: ExerciseCode
  duzey: number
  tohum: string
  bitti: boolean
}

export function Exercise({
  onExit,
  odev,
  onGonder,
  listeVar = false,
  biten,
  sira,
  toplam,
}: {
  onExit: () => void
  /**
   * Bağlı mod: uygulayıcının verdiği ödev.
   *
   * Verilirse şablon seçici ve seviye düğmeleri görünmüyor — çocuğun
   * önüne menü koymanın anlamı yok, bağlantı zaten o ödev için verildi.
   * Sayfa sunucunun gönderdiği TOHUMDAN üretiliyor; böylece puanlarken
   * sunucu aynı sayfayı yeniden üretip aynı şeyi görüyor.
   */
  odev?: OdevTarifi
  /** Ham cevabı sunucuya yollar; sonuç çocuğa gösterilmez */
  onGonder?: (cevap: unknown, sureMs: number) => Promise<void>
  /**
   * Arkada bir liste var mı — haftalık bağlantıdan gelindiyse var.
   *
   * Bitiş ekranı tek ödevlik bağlantı (`?e=`) için yazılmıştı ve
   * "bu sayfayı kapatabilirsin" diyordu. Haftalık bağlantıda (`?h=`)
   * aynı ekran çıkmazdı sona çeviriyordu: çocuğun önünde dört çalışma
   * daha duruyor ama geri dönecek yer yok, uygulayıcının QR'ı yeniden
   * okutması gerekiyordu. Haftalık bağlantının var oluş sebebi tek
   * okutmaydı; o yüzden bitiş ekranı listeye dönüşü göstermeli.
   */
  listeVar?: boolean
  /**
   * Bu çalışma bitince hafta içinde kaç tanesi bitmiş olacak.
   *
   * Geçiş ekranı bunu yazıyor (`dokuman/gorsellik.md` §3). Çocuğa
   * "ne kadar kaldı" bilgisini veren tek yer burası: listeye dönünce
   * görüyor ama arada bir ekran var ve o ekran boş durmamalı.
   */
  biten?: number
  /**
   * Çerçevedeki ilerleme: haftanın kaçıncı çalışması, kaç tanesi var.
   *
   * Listenin sırası sunucuda sabit (`exerciseCode`'a göre), o yüzden
   * aynı hafta her açılışta aynı numarayı gösteriyor. Tek ödevlik
   * bağlantıda (`?e=`) ikisi de yok: arkada liste olmadığı için "1 / 1"
   * yazmak çocuğa olmayan bir bütünü ima ediyor.
   */
  sira?: number
  toplam?: number
}) {
  const bagli = Boolean(odev)
  const [code, setCode] = useState<ExerciseCode | null>(odev?.kod ?? null)
  const [level, setLevel] = useState(odev?.duzey ?? 5)
  const [round, setRound] = useState(0)
  const [result, setResult] = useState<GradeResult | null>(null)
  const [gonderimDurumu, setGonderimDurumu] = useState<'bekliyor' | 'gidiyor' | 'bitti' | 'hata'>(
    'bekliyor',
  )
  const baslangic = useRef<number>(Date.now())

  const instance = useMemo<ExerciseInstance | null>(
    () =>
      code
        ? generateExercise(code, odev ? BigInt(odev.tohum) : hashOf(code, level, round), level)
        : null,
    [code, level, round, odev],
  )

  /**
   * Bağlı modda cevap sunucuya gidiyor ve sonuç ÇOCUĞA GÖSTERİLMİYOR.
   *
   * Egzersiz bir sınav değil; "12'de 7" görmek çalışmayı performansa
   * çeviriyor. Çocuk bitirdiğini görür, değerlendirmeyi uygulayıcı yapar.
   */
  const sonucAl = (r: GradeResult, cevap: unknown) => {
    if (!bagli) {
      setResult(r)
      return
    }
    setGonderimDurumu('gidiyor')
    void onGonder?.(cevap, Date.now() - baslangic.current)
      .then(() => setGonderimDurumu('bitti'))
      .catch(() => setGonderimDurumu('hata'))
  }

  const reset = () => setResult(null)

  /**
   * Hata ayıklama kancası: `?debug` ile açılır.
   *
   * Üretilen sayfayı ve çözümünü pencereye asar. Destek tarafında
   * "bu sayfa niye böyle çıktı" sorusunu cevaplamanın ve arayüzü
   * otomatik sınamanın tek pratik yolu bu. Adresinde `debug` yoksa
   * hiçbir şey yazılmaz.
   */
  useEffect(() => {
    if (!new URLSearchParams(location.search).has('debug')) return
    const id = 'nirengi-debug'
    let el = document.getElementById(id)
    if (!el) {
      el = document.createElement('script')
      el.id = id
      el.setAttribute('type', 'application/json')
      document.body.appendChild(el)
    }
    // DOM'a yazıyoruz, window'a değil: tarayıcı araçları sayfanın JS
    // global alanını göremiyor ama DOM'u paylaşıyor.
    el.textContent = JSON.stringify(
      instance ? { code, level, mode: modeOf(code!, instance), instance } : null,
    )
  }, [code, level, instance])

  if (!code || !instance) {
    return (
      <div className="menu">
        <h1>Egzersizler</h1>
        <p>{EXERCISE_CODES.length} şablon. Her açılışta yeni bir sayfa üretilir.</p>
        {EXERCISE_CODES.map((c) => (
          <div
            key={c}
            className="card"
            onClick={() => {
              setCode(c)
              setRound((r) => r + 1)
              reset()
            }}
          >
            <div className="t">{EXERCISES[c].title}</div>
            <div className="d">
              {EXERCISES[c].domains.join(', ')} · {EXERCISES[c].channels.join('/')}
            </div>
          </div>
        ))}
        <button onClick={onExit}>Geri</button>
      </div>
    )
  }

  const mode = modeOf(code, instance)

  if (bagli && gonderimDurumu === 'bitti') {
    return (
      <div className="ex">
        <Serit kod={code} sira={sira} toplam={toplam} />
        <div className="bitti">
          <h2>Bitti, eline sağlık</h2>
          {/*
            İlerleme SAYI, çubuk değil — çerçevedekiyle aynı gerekçe:
            çubuk kalan işi değil kalan süreyi anlatıyor gibi duruyor.
            Puan, yıldız, rozet YOK (§3): ödül çocuğu doğru cevaptan
            hızlı cevaba kaydırıyor ve hız da ölçtüğümüz şeylerden
            biri. Övgüyü uygulayıcı ve veli verir.
          */}
          {listeVar && biten !== undefined && toplam !== undefined && (
            <p className="ilerleme">
              {toplam} çalışmadan <b>{biten}</b> tanesi bitti
            </p>
          )}
          <p>
            {listeVar
              ? 'Çalışman kaydedildi. Listede başka çalışmalar da var.'
              : 'Çalışman kaydedildi. Bu sayfayı kapatabilirsin.'}
          </p>
          {listeVar && (
            <button className="don" onClick={onExit}>
              Listeye dön
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="ex">
      <Serit
        kod={code}
        sira={sira}
        toplam={toplam}
        sag={
          bagli ? undefined : (
            <span className="seviye">
              seviye
              <button className="mini" onClick={() => { setLevel((l) => Math.max(1, l - 1)); reset() }}>
                −
              </button>
              <b>{level}</b>
              <button className="mini" onClick={() => { setLevel((l) => Math.min(10, l + 1)); reset() }}>
                +
              </button>
            </span>
          )
        }
      />

      {bagli && gonderimDurumu === 'gidiyor' && <p className="nasil">Kaydediliyor…</p>}
      {bagli && gonderimDurumu === 'hata' && (
        <p className="nasil" style={{ color: '#c0392b' }}>
          Kaydedilemedi. İnternet bağlantını kontrol edip tekrar dene.
        </p>
      )}

      <p className="prompt">{instance.prompt}</p>
      <p className="nasil">{EXERCISE_HINT[code]}</p>

      {instance.spokenPrompt && <Listen text={instance.spokenPrompt} />}

      <Runner
        key={`${code}-${level}-${round}`}
        code={code}
        instance={instance}
        mode={mode}
        result={result}
        onResult={sonucAl}
      />

      {!bagli && (
        <div className="controls">
          <button onClick={() => { setRound((r) => r + 1); reset() }}>Yeni sayfa</button>
          <button onClick={() => { setCode(null); reset() }}>Listeye dön</button>
        </div>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * İşitsel yönerge
 * ------------------------------------------------------------------ */

function Listen({ text }: { text: string }) {
  const [spoken, setSpoken] = useState(0)
  const [noVoice, setNoVoice] = useState(false)

  const speak = () => {
    if (!('speechSynthesis' in window)) {
      setNoVoice(true)
      return
    }
    window.speechSynthesis.cancel()
    const u = new SpeechSynthesisUtterance(text)
    u.lang = 'tr-TR'
    u.rate = 0.95
    const tr = window.speechSynthesis.getVoices().find((v) => v.lang.startsWith('tr'))
    if (tr) u.voice = tr
    else setNoVoice(true)
    window.speechSynthesis.speak(u)
    setSpoken((n) => n + 1)
  }

  return (
    <div style={{ display: 'grid', gap: 8, margin: '4px 0 14px' }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <button className="primary" onClick={speak}>
          ▶ Dinle
        </button>
        <span className="note" style={{ textAlign: 'left' }}>
          {spoken === 0 ? 'Yönergeyi dinlemek için bas.' : `${spoken} kez dinlendi`}
        </span>
      </div>
      {noVoice && (
        <p className="note" style={{ textAlign: 'left', color: 'var(--warn)' }}>
          Bu cihazda Türkçe ses bulunamadı. Uygulayıcı yönergeyi sesli okumalı:
          <br />
          <b>{text}</b>
        </p>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * Etkileşim biçimleri
 * ------------------------------------------------------------------ */

/**
 * DISARI ACIK: sinamalarin surukledigi birim bu.
 *
 * `Exercise` yonlendirme, sunucu cagrisi ve menu tasiyor; `Runner`
 * yalnizca props aliyor ve `onResult` ile puani geri veriyor. Ekran
 * kanalinin sozlesmesi tam olarak burada: cizim agaci iceri giriyor,
 * cocugun verdigi cevap disari cikiyor.
 */
export function Runner({
  code,
  instance,
  mode,
  result,
  onResult,
}: {
  code: ExerciseCode
  instance: ExerciseInstance
  mode: Mode
  result: GradeResult | null
  onResult: (r: GradeResult, cevap: unknown) => void
}) {
  if (mode === 'cevir') return <FlipGame instance={instance} result={result} onResult={onResult} />
  if (mode === 'kelime') return <WordHunt instance={instance} result={result} onResult={onResult} />
  if (mode === 'dizi') return <SpanRecall instance={instance} result={result} onResult={onResult} />
  if (mode === 'sihirbaz')
    return <Wizard code={code} instance={instance} result={result} onResult={onResult} />
  if (mode === 'cizgi') return <TraceMaze instance={instance} result={result} onResult={onResult} />
  if (mode === 'damgala') return <StampBoard instance={instance} result={result} onResult={onResult} />
  if (mode === 'iz') return <IzSurme instance={instance} result={result} onResult={onResult} />
  if (mode === 'kopyala')
    return <NoktaKopyala instance={instance} result={result} onResult={onResult} />
  if (mode === 'bak')
    return <Sahne code={code} instance={instance} result={result} onResult={onResult} />
  if (mode === 'gizli')
    return <GizliSekil instance={instance} result={result} onResult={onResult} />
  if (mode === 'kule') return <Kule instance={instance} result={result} onResult={onResult} />
  if (mode === 'koridor')
    return <IzKoridor instance={instance} result={result} onResult={onResult} />
  if (mode === 'ritim') return <Ritim instance={instance} result={result} onResult={onResult} />
  return <MarkOrType code={code} instance={instance} mode={mode} result={result} onResult={onResult} />
}

/* ---------- sihirbaz: tek tek sor ---------- */

/**
 * Maddeleri tek tek sorar: büyük bir soru, altında seçenekler.
 *
 * On iki kelimeyi yan yana kutularla göstermek, çocuğun önce hangi
 * kutuya dokunacağını seçmesini gerektiriyordu; asıl görevin (kelimeyi
 * gruba yerleştirmek) üstüne gereksiz bir yük. Tek tek sorulunca o yük
 * kalkıyor ve her maddeye verilen süre de ayrı ayrı ölçülebilir hale
 * geliyor — ileride hız verisi için gerekli olacak.
 */
function Wizard({
  code,
  instance,
  result,
  onResult,
}: {
  code: ExerciseCode
  instance: ExerciseInstance
  result: GradeResult | null
  onResult: (r: GradeResult, cevap: unknown) => void
}) {
  const rows = instance.render.find((n) => n.t === 'rows') as
    | Extract<RenderNode, { t: 'rows' }>
    | undefined
  const [at, setAt] = useState(0)
  const [answers, setAnswers] = useState<string[]>([])
  const locked = result !== null

  if (!rows) return null
  const items = rows.items
  const palette = rows.palette ?? []

  if (locked) {
    return (
      <>
        <Score result={result} />
        <div className="erows auto">
          {items.map((it, i) => (
            <div className="erow" key={i}>
              <span className="lb">
                <Uyaran shape={it.labelShape} glyph={it.label} />
              </span>
              <span
                className="slot"
                style={{ color: result.perItem?.[i] ? 'var(--ok)' : 'var(--bad)' }}
              >
                <Dizi metin={answers[i] ?? ''} />
              </span>
            </div>
          ))}
        </div>
      </>
    )
  }

  const choose = (g: string) => {
    const next = [...answers]
    next[at] = g
    setAnswers(next)
    if (at + 1 < items.length) setAt(at + 1)
    else onResult(getExercise(code).grade(instance, next), next)
  }

  return (
    <>
      <p className="note" style={{ textAlign: 'left' }}>
        {at + 1} / {items.length}
      </p>
      <div
        style={{
          minHeight: 110,
          display: 'grid',
          placeItems: 'center',
          background: 'var(--panel)',
          border: '1px solid var(--line)',
          borderRadius: 12,
          margin: '8px 0 16px',
          fontSize: 34,
          fontWeight: 600,
        }}
      >
        <Uyaran shape={items[at]?.labelShape} glyph={items[at]?.label} />
      </div>

      <div className="controls" style={{ borderTop: 'none', padding: 0 }}>
        {palette.map((g) => (
          <button
            key={kimlik(g)}
            className="primary"
            style={{ flex: '1 1 120px' }}
            onClick={() => choose(kimlik(g))}
          >
            <Uyaran {...g} />
          </button>
        ))}
      </div>

      {at > 0 && (
        <div className="controls">
          <button className="mini" onClick={() => setAt(at - 1)}>
            ← Geri
          </button>
        </div>
      )}
    </>
  )
}

/* ---------- dizi: göster, KAPAT, sonra sor ---------- */

/**
 * Dizi hatırlama bir bellek görevi; uyarıcı ekranda kalırsa belleği
 * değil kopyalamayı ölçer. Kağıtta çocuk sayfayı kapatır, ekranda
 * uygulamanın kapatması gerekir.
 *
 * Diziler ayrıca TEK TEK sunuluyor: beşi birden gösterilince çocuk
 * hangisine bakacağını bilemiyor ve görev dizi belleği olmaktan çıkıp
 * tarama görevine dönüşüyor.
 */
function SpanRecall({
  instance,
  result,
  onResult,
}: {
  instance: ExerciseInstance
  result: GradeResult | null
  onResult: (r: GradeResult, cevap: unknown) => void
}) {
  const grid = instance.render.find((n) => n.t === 'grid') as
    | Extract<RenderNode, { t: 'grid' }>
    | undefined
  const rowsNode = instance.render.find((n) => n.t === 'rows') as
    | Extract<RenderNode, { t: 'rows' }>
    | undefined

  // Izgara satır satır dizileri taşıyor: son sütun ayraç
  const sequences = useMemo(() => {
    if (!grid) return []
    const out: string[][] = []
    for (let r = 0; r < grid.rows; r++) {
      const row: string[] = []
      for (let c = 0; c < grid.cols - 1; c++) {
        const h = grid.cells[r * grid.cols + c]
        const g = h?.shape ?? h?.glyph
        if (g) row.push(g)
      }
      if (row.length) out.push(row)
    }
    return out
  }, [grid])

  const reversed = instance.prompt.includes('TERSTEN')
  const palette = rowsNode?.palette ?? []

  const [round, setRound] = useState(0)
  const [phase, setPhase] = useState<'hazir' | 'goster' | 'cevapla'>('hazir')
  const [shown, setShown] = useState(-1)
  const [answers, setAnswers] = useState<string[]>([])
  const [current, setCurrent] = useState<string[]>([])
  const locked = result !== null

  const play = async () => {
    setPhase('goster')
    const seq = sequences[round] ?? []
    for (let i = 0; i < seq.length; i++) {
      setShown(i)
      await new Promise((r) => setTimeout(r, 700))
      setShown(-1)
      await new Promise((r) => setTimeout(r, 180))
    }
    setPhase('cevapla')
    setCurrent([])
  }

  const submitRound = () => {
    const next = [...answers]
    next[round] = (reversed ? [...current].reverse() : current).join(' ')
    setAnswers(next)
    setCurrent([])
    if (round + 1 < sequences.length) {
      setRound(round + 1)
      setPhase('hazir')
    } else {
      onResult(getExercise('span_recall').grade(instance, next), next)
    }
  }

  if (locked) {
    return (
      <>
        <Score result={result} />
        <table style={{ width: '100%', fontSize: 14, borderCollapse: 'collapse' }}>
          <tbody>
            {sequences.map((s, i) => (
              <tr key={i}>
                <td style={{ padding: 6, color: 'var(--muted)' }}>{i + 1}.</td>
                <td style={{ padding: 6 }}>
                  <Dizi metin={s.join(' ')} />
                </td>
                <td
                  style={{
                    padding: 6,
                    color: result.perItem?.[i] ? 'var(--ok)' : 'var(--bad)',
                  }}
                >
                  <Dizi metin={answers[i] ?? ''} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
    )
  }

  const seq = sequences[round] ?? []

  return (
    <>
      <p className="note" style={{ textAlign: 'left' }}>
        {round + 1}. dizi / {sequences.length}
        {reversed ? ' · TERSTEN söyleyeceksin' : ''}
      </p>

      <div
        style={{
          minHeight: 120,
          display: 'grid',
          placeItems: 'center',
          background: 'var(--panel)',
          border: '1px solid var(--line)',
          borderRadius: 12,
          margin: '8px 0 14px',
        }}
      >
        {phase === 'hazir' && <span className="note">Hazır olunca başlat</span>}
        {phase === 'goster' && (
          <span className="tek" style={{ fontSize: 64, lineHeight: 1 }}>
            {shown >= 0 ? <UyaranAdi ad={seq[shown] ?? ''} /> : ' '}
          </span>
        )}
        {phase === 'cevapla' && (
          <span style={{ fontSize: 32, letterSpacing: 6 }}>
            <Dizi metin={current.join(' ')} bos="…" />
          </span>
        )}
      </div>

      {phase === 'hazir' && (
        <div className="controls">
          <button className="primary wide" onClick={play}>
            Diziyi göster
          </button>
        </div>
      )}

      {phase === 'cevapla' && (
        <>
          <div className="palette" style={{ position: 'static' }}>
            {palette.map((g) => (
              <button
                key={kimlik(g)}
                className="mini glyph-btn"
                onClick={() => setCurrent((c) => [...c, kimlik(g)])}
              >
                <Uyaran {...g} />
              </button>
            ))}
            <button className="mini" onClick={() => setCurrent((c) => c.slice(0, -1))}>
              ⌫
            </button>
          </div>
          <div className="controls">
            <button className="primary wide" disabled={current.length === 0} onClick={submitRound}>
              {round + 1 < sequences.length ? 'Sonraki dizi' : 'Bitir'}
            </button>
          </div>
        </>
      )}
    </>
  )
}

/* ---------- kelime: iki dokunuşla kelime seç ---------- */

/**
 * Kelime bulmacada hücreleri tek tek işaretletmek yanlıştı: iki kelime
 * bir harfte kesiştiğinde ikinci kelimeyi seçmek o harfi kapatıyor ve
 * birinci kelime bozuluyordu.
 *
 * Doğrusu kelime seçmek: ilk harfe dokun, son harfe dokun; aradaki düz
 * çizgi kelimeyi verir. Kesişen harf iki kelimeye birden ait olabilir.
 */
function WordHunt({
  instance,
  result,
  onResult,
}: {
  instance: ExerciseInstance
  result: GradeResult | null
  onResult: (r: GradeResult, cevap: unknown) => void
}) {
  const { words } = instance.solution as { words: string[] }
  const grid = instance.render.find((n) => n.t === 'grid') as
    | Extract<RenderNode, { t: 'grid' }>
    | undefined
  const [anchor, setAnchor] = useState<number | null>(null)
  const [runs, setRuns] = useState<{ word: string; cells: string[] }[]>([])
  const locked = result !== null

  if (!grid) return null
  const cols = grid.cols
  const letters = grid.cells.map((c) => c.glyph ?? '')

  const fold = (s: string) =>
    s.toLocaleLowerCase('tr-TR')
      .replace(/ç/g, 'c').replace(/ğ/g, 'g').replace(/ı/g, 'i')
      .replace(/ö/g, 'o').replace(/ş/g, 's').replace(/ü/g, 'u')

  const lineBetween = (a: number, b: number): number[] | null => {
    const ar = Math.floor(a / cols), ac = a % cols
    const br = Math.floor(b / cols), bc = b % cols
    const dr = Math.sign(br - ar), dc = Math.sign(bc - ac)
    const len = Math.max(Math.abs(br - ar), Math.abs(bc - ac)) + 1
    // Yalnız yatay, dikey ve 45° çapraz
    if (Math.abs(br - ar) !== Math.abs(bc - ac) && br !== ar && bc !== ac) return null
    const out: number[] = []
    for (let i = 0; i < len; i++) out.push((ar + dr * i) * cols + (ac + dc * i))
    return out
  }

  const found = new Set(runs.map((r) => fold(r.word)))
  const marked = new Set(runs.flatMap((r) => r.cells))

  const tap = (i: number) => {
    if (locked) return
    if (anchor === null) {
      setAnchor(i)
      return
    }
    if (anchor === i) {
      setAnchor(null)
      return
    }
    const line = lineBetween(anchor, i)
    setAnchor(null)
    if (!line) return
    const word = line.map((k) => letters[k] ?? '').join('')
    const hit = words.find((w) => fold(w) === fold(word) || fold(w) === fold([...word].reverse().join('')))
    if (!hit || found.has(fold(hit))) return
    const cells = (fold(hit) === fold(word) ? line : [...line].reverse()).map((k) => 'w' + k)
    setRuns((r) => [...r, { word: hit, cells }])
  }

  return (
    <>
      <p className="note" style={{ textAlign: 'left' }}>
        Kelimenin ilk harfine, sonra son harfine dokun.
      </p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, margin: '6px 0 12px' }}>
        {words.map((w) => (
          <span
            key={w}
            style={{
              fontWeight: 600,
              textDecoration: found.has(fold(w)) ? 'line-through' : 'none',
              color: found.has(fold(w)) ? 'var(--ok)' : 'var(--ink)',
            }}
          >
            {w}
          </span>
        ))}
      </div>
      <div className="egrid" style={{ gridTemplateColumns: `repeat(${cols}, auto)` }}>
        {grid.cells.map((c, i) => (
          <div
            key={i}
            className={
              'c' + (marked.has('w' + i) ? ' sel' : '') + (anchor === i ? ' anchor' : '')
            }
            onClick={() => tap(i)}
          >
            {c.glyph ?? ''}
          </div>
        ))}
      </div>
      <Score result={result} />
      {!locked && (
        <div className="controls">
          <button className="primary wide" onClick={() => onResult(getExercise('word_search').grade(instance, runs), runs)}>
            Kontrol et
          </button>
        </div>
      )}
    </>
  )
}

/* ---------- çizgi: labirentte yol çiz ---------- */

function TraceMaze({
  instance,
  result,
  onResult,
}: {
  instance: ExerciseInstance
  result: GradeResult | null
  onResult: (r: GradeResult, cevap: unknown) => void
}) {
  /**
   * Çözüm iki biçimde gelebiliyor.
   *
   * `maze` düz bir kutu dizisi veriyor; `rule_maze` ise yanında sınırı
   * ve sayıları da taşıdığı için nesne. Uçları ikisinden de aynı
   * şekilde okumak gerekiyor — `solution[0]` nesnede `undefined`
   * dönüyor ve labirent 0. kutudan başlıyormuş gibi davranıyordu.
   */
  const ham = instance.solution as number[] | { yol: number[]; start: number; end: number }
  const solution = Array.isArray(ham) ? ham : ham.yol
  const start = Array.isArray(ham) ? (solution[0] ?? 0) : ham.start
  const end = Array.isArray(ham) ? (solution[solution.length - 1] ?? 0) : ham.end
  const cols = Number(instance.params['cols'] ?? 8)
  const [path, setPath] = useState<number[]>([start])
  const locked = result !== null

  const grid = instance.render.find((n) => n.t === 'grid') as
    | Extract<RenderNode, { t: 'grid' }>
    | undefined
  if (!grid) return null

  const step = (to: number) => {
    if (locked) return
    const last = path[path.length - 1]!
    // Geri alma: bir önceki kutuya dokunmak son adımı siler
    if (path.length > 1 && to === path[path.length - 2]) {
      setPath(path.slice(0, -1))
      return
    }
    if (path.includes(to)) return

    const diff = to - last
    const sameRow = Math.abs(diff) === 1 && Math.floor(to / cols) === Math.floor(last / cols)
    const sameCol = Math.abs(diff) === cols
    if (!sameRow && !sameCol) return

    // Aradaki duvar kapalıysa geçilemez — kural ekranda da geçerli
    const dir = diff === -cols ? 0 : diff === 1 ? 1 : diff === cols ? 2 : 3
    const walls = grid.cells[last]?.walls
    if (!walls || walls[dir]) return

    setPath([...path, to])
  }

  const done = path[path.length - 1] === end

  /**
   * Kutu boyu sütun sayısına göre.
   *
   * Sabit 22 pikseldi ve bu, yeni ekran kiplerinde ısrar ettiğimiz 44
   * piksellik dokunma hedefinin yarısı: küçük bir labirentte çocuk
   * yanlış kutuya basıyor ve bunu geri almak da bir dokunuş. Sayılı
   * labirentte ayrıca kutuda bir rakam var ve 22 pikselde okunmuyor.
   * Büyük labirentte (16 sütun) küçülüyor, yoksa sayfaya sığmıyor.
   */
  const kenar = Math.max(22, Math.min(40, Math.floor(380 / grid.cols)))
  const olcu = { width: kenar, height: kenar, fontSize: Math.max(9, Math.round(kenar * 0.42)) }

  return (
    <>
      <p className="note" style={{ textAlign: 'left' }}>
        A'dan başlayıp komşu kutulara dokunarak ilerle. Geri almak için bir önceki
        kutuya dokun.
      </p>
      <div className="egrid maze" style={{ gridTemplateColumns: `repeat(${grid.cols}, auto)` }}>
        {grid.cells.map((c, i) => (
          <div
            key={i}
            className={'c' + (path.includes(i) ? ' trail' : '')}
            style={{
              borderTop: c.walls?.[0] ? '2px solid #38414f' : '2px solid transparent',
              borderRight: c.walls?.[1] ? '2px solid #38414f' : '2px solid transparent',
              borderBottom: c.walls?.[2] ? '2px solid #38414f' : '2px solid transparent',
              borderLeft: c.walls?.[3] ? '2px solid #38414f' : '2px solid transparent',
              ...olcu,
              cursor: 'pointer',
            }}
            onClick={() => step(i)}
          >
            <Uyaran shape={c.shape} glyph={c.glyph} />
          </div>
        ))}
      </div>
      <Score result={result} />
      {!locked && (
        <div className="controls">
          <button className="mini" onClick={() => setPath([start])}>
            Baştan
          </button>
          <button
            className="primary wide"
            disabled={!done}
            onClick={() => onResult(getExercise(instance.code).grade(instance, path), path)}
          >
            {done ? 'Kontrol et' : "B'ye ulaş"}
          </button>
        </div>
      )}
    </>
  )
}

function Score({ result }: { result: GradeResult | null }) {
  if (!result) return null
  return (
    <p className="big" style={{ color: result.accuracy > 0.8 ? 'var(--ok)' : 'var(--warn)' }}>
      {result.correct} / {result.total} doğru
    </p>
  )
}

/* ---------- işaretle / yaz ---------- */

function MarkOrType({
  code,
  instance,
  mode,
  result,
  onResult,
}: {
  code: ExerciseCode
  instance: ExerciseInstance
  mode: Mode
  result: GradeResult | null
  onResult: (r: GradeResult, cevap: unknown) => void
}) {
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [typed, setTyped] = useState<Record<string, string>>({})
  const [focus, setFocus] = useState<string | null>(null)
  const locked = result !== null

  const toggle = (id: string) => {
    if (locked) return
    setPicked((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const check = () => {
    const answer = mode === 'yaz' ? orderedAnswers(instance, typed) : [...picked]
    onResult(getExercise(code).grade(instance, answer), answer)
  }

  return (
    <>
      {instance.render.map((n, i) => (
        <Node
          key={i}
          node={n}
          sira={i}
          picked={picked}
          typed={typed}
          focus={focus}
          locked={locked}
          onToggle={toggle}
          onType={(id, v) => setTyped((t) => ({ ...t, [id]: v }))}
          onFocus={setFocus}
        />
      ))}
      <Score result={result} />
      {!locked && (
        <div className="controls">
          <button className="primary wide" onClick={check}>
            Kontrol et
          </button>
        </div>
      )}
    </>
  )
}

/* ---------- damgala: paletten şekil seç, hücreye bas ---------- */

function StampBoard({
  instance,
  result,
  onResult,
}: {
  instance: ExerciseInstance
  result: GradeResult | null
  onResult: (r: GradeResult, cevap: unknown) => void
}) {
  const palette = useMemo(() => {
    if (instance.code === 'sudoku_mini') {
      const size = Number(instance.params['size'] ?? 4)
      return Array.from({ length: size }, (_, i) => String(i + 1))
    }
    /**
     * İşlem karesinde palet motordan geliyor: boşluk bir sayı ya da
     * bir işaret olabiliyor ve hangisi olduğunu şablon biliyor.
     * Burada yeniden türetmek, aynı bilgiyi iki yerde tutmak olurdu.
     */
    if (instance.code === 'arith_grid') {
      const rows = instance.render.find((n) => n.t === 'rows') as
        | Extract<RenderNode, { t: 'rows' }>
        | undefined
      return (rows?.palette ?? []).map((x) => x.glyph ?? '')
    }
    const sol = instance.solution as { cell: string; shape: string }[]
    return [...new Set(sol.map((s) => s.shape))]
  }, [instance])
  const [brush, setBrush] = useState<string>(palette[0] ?? '')
  /**
   * Seçili fırça ayrıca bir referansta tutuluyor.
   *
   * Sebebi gerçek bir hata: şekli seçip hemen kutuya basınca hücrenin
   * tıklama işleyicisi hâlâ BİR ÖNCEKİ fırçayı görüyordu — React yeniden
   * çizmeye fırsat bulamadan ikinci dokunuş geliyor. Çocuk hızlı
   * dokunduğunda yanlış sembol basılıyordu ve bunu fark etmesi zor.
   */
  const brushRef = useRef(brush)
  const pick = (g: string) => {
    brushRef.current = g
    setBrush(g)
  }
  const [board, setBoard] = useState<Record<string, string>>({})
  const locked = result !== null

  const grid = instance.render.find((n) => n.t === 'grid') as
    | Extract<RenderNode, { t: 'grid' }>
    | undefined
  if (!grid) return null

  return (
    <>
      <p className="note" style={{ textAlign: 'left' }}>
        Önce aşağıdan birini seç, sonra boş kutuya bas.
      </p>
      <div className="echoices">
        {palette.map((g) => (
          <div
            key={g}
            className={'echoice' + (brush === g ? ' sel' : '')}
            onClick={() => !locked && pick(g)}
          >
            <UyaranAdi ad={g} />
          </div>
        ))}
        <div className="echoice" onClick={() => !locked && pick('')} title="sil">
          ⌫
        </div>
      </div>

      <div className="egrid" style={{ gridTemplateColumns: `repeat(${grid.cols}, auto)` }}>
        {grid.cells.map((c, i) => {
          const id = c.id ?? 'g' + i
          // Verili ipuçları (sudokuda) değiştirilemez ve olduğu gibi durur
          const fixed = !c.id
          return (
            <div
              key={i}
              className={'c' + (board[id] ? ' sel' : '') + (fixed ? ' given' : '')}
              onClick={() => {
                if (locked || fixed) return
                const g = brushRef.current
                setBoard((b) => {
                  const next = { ...b }
                  if (g === '') delete next[id]
                  else next[id] = g
                  return next
                })
              }}
            >
              {fixed ? <Uyaran shape={c.shape} glyph={c.glyph} /> : <UyaranAdi ad={board[id] ?? ''} />}
            </div>
          )
        })}
      </div>

      <Score result={result} />
      {!locked && (
        <div className="controls">
          <button className="primary wide" onClick={() => onResult(getExercise(instance.code).grade(instance, board), board)}>
            Kontrol et
          </button>
        </div>
      )}
    </>
  )
}

/* ---------- çevir: kapalı kartlar ---------- */

function FlipGame({
  instance,
  result,
  onResult,
}: {
  instance: ExerciseInstance
  result: GradeResult | null
  onResult: (r: GradeResult, cevap: unknown) => void
}) {
  const deck = instance.solution as string[]
  const [open, setOpen] = useState<number[]>([])
  const [matched, setMatched] = useState<string[]>([])
  const [tries, setTries] = useState(0)
  const timer = useRef<number | null>(null)

  const grid = instance.render.find((n) => n.t === 'grid') as
    | Extract<RenderNode, { t: 'grid' }>
    | undefined

  useEffect(() => {
    return () => {
      if (timer.current) window.clearTimeout(timer.current)
    }
  }, [])

  // Hepsi bulununca kendiliğinden biter: "Kontrol et" beklemeye gerek yok
  useEffect(() => {
    const pairs = new Set(deck).size
    if (matched.length === pairs && pairs > 0 && !result) {
      onResult(getExercise('memory_pairs').grade(instance, matched), matched)
    }
  }, [matched, deck, instance, onResult, result])

  if (!grid) return null

  const flip = (i: number) => {
    if (result) return
    if (open.includes(i) || matched.includes(deck[i]!)) return
    if (open.length === 2) return

    const next = [...open, i]
    setOpen(next)
    if (next.length === 2) {
      setTries((t) => t + 1)
      const [a, b] = next
      if (deck[a!] === deck[b!]) {
        setMatched((m) => [...m, deck[a!]!])
        setOpen([])
      } else {
        timer.current = window.setTimeout(() => setOpen([]), 900)
      }
    }
  }

  return (
    <>
      <p className="note" style={{ textAlign: 'left' }}>
        {matched.length} çift bulundu · {tries} deneme
      </p>
      <div className="egrid" style={{ gridTemplateColumns: `repeat(${grid.cols}, auto)` }}>
        {deck.map((g, i) => {
          const shown = open.includes(i) || matched.includes(g) || result !== null
          return (
            <div
              key={i}
              className={'c card-face' + (shown ? ' up' : '')}
              onClick={() => flip(i)}
            >
              {shown ? g : '?'}
            </div>
          )
        })}
      </div>
      <Score result={result} />
    </>
  )
}

/* ------------------------------------------------------------------ */

function orderedAnswers(instance: ExerciseInstance, typed: Record<string, string>): string[] {
  const ids: string[] = []
  for (const node of instance.render) {
    if (node.t === 'rows') for (const it of node.items) if (it.id) ids.push(it.id)
  }
  return ids.map((id) => typed[id] ?? '')
}

function hashOf(code: string, level: number, round: number): number {
  let h = 2166136261
  const s = `${code}|${level}|${round}`
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

function Node({
  node,
  /**
   * Dugumun agactaki sirasi — `clipPath` kimliklerini tekil tutuyor.
   *
   * Sayfada birden cok goz cizimi oldugunda (eslestirme sorusu) ayni
   * kimlik iki kez geciyor ve tarayici ilkini kullaniyor: ikinci goz
   * birincinin kapagiyla kirpiliyordu.
   */
  sira = 0,
  picked,
  typed,
  focus,
  locked,
  onToggle,
  onType,
  onFocus,
}: {
  node: RenderNode
  sira?: number
  picked: Set<string>
  typed: Record<string, string>
  focus: string | null
  locked: boolean
  onToggle: (id: string) => void
  onType: (id: string, v: string) => void
  onFocus: (id: string | null) => void
}) {
  switch (node.t) {
    case 'text':
      return (
        <p
          style={{
            fontSize: node.size === 'lg' ? 28 : node.size === 'sm' ? 13 : 16,
            fontWeight: node.bold ? 700 : 400,
          }}
        >
          {node.value}
        </p>
      )

    case 'grid': {
      const isMaze = node.cells.some((c) => c.walls)
      return (
        <div
          className={'egrid' + (isMaze ? ' maze' : '')}
          style={{ gridTemplateColumns: `repeat(${node.cols}, auto)`, gap: node.gap ?? 4 }}
        >
          {node.cells.map((c, i) => {
            const id = c.id ?? 'x' + i
            const style = c.walls
              ? {
                  borderTop: c.walls[0] ? '2px solid #38414f' : '2px solid transparent',
                  borderRight: c.walls[1] ? '2px solid #38414f' : '2px solid transparent',
                  borderBottom: c.walls[2] ? '2px solid #38414f' : '2px solid transparent',
                  borderLeft: c.walls[3] ? '2px solid #38414f' : '2px solid transparent',
                  width: 20,
                  height: 20,
                }
              : undefined
            return (
              <div
                key={i}
                className={'c' + (picked.has(id) ? ' sel' : '') + (c.blank ? ' blank' : '')}
                style={style}
                onClick={() => !locked && c.id && onToggle(id)}
              >
                <Uyaran shape={c.shape} glyph={c.glyph} />
              </div>
            )
          })}
        </div>
      )
    }

    case 'paths': {
      // Etiketler SVG'nin DIŞINDA: içeride olsalardı preserveAspectRatio
      // "none" onları da esnetir ve harfler basık çıkardı.
      const h = node.height ?? 200
      return (
        <div className="pathwrap" style={{ height: h }}>
          {/* Her çizgi önce arka plan renginde kalın bir "hale" ile, sonra
              kendisiyle çiziliyor. Kesişmede sonraki çizginin halesi
              öncekini kesiyor ve hangisinin üstten geçtiği görünüyor —
              hepsi aynı renkte üst üste binince yol takip edilemiyordu. */}
          <svg viewBox="0 0 1000 1000" preserveAspectRatio="none">
            {node.items.map((p, i) => {
              const pts = p.points.map(([x, y]) => `${x * 1000},${y * 1000}`).join(' ')
              return (
                <g key={i}>
                  <polyline
                    points={pts}
                    fill="none"
                    stroke="#ffffff"
                    strokeWidth={10}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    vectorEffect="non-scaling-stroke"
                  />
                  <polyline
                    points={pts}
                    fill="none"
                    stroke={p.color ?? '#2563c9'}
                    strokeWidth={3}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    vectorEffect="non-scaling-stroke"
                    /*
                     * Çizgi biçimi EKRANDA DA çiziliyor.
                     *
                     * Katman 0'da yol takibine renkten bağımsız bir
                     * ipucu eklenmişti (`dash`) çünkü ev yazıcısı
                     * siyah-beyaz basıyor. Kâğıt oluşturucu onu
                     * çiziyordu ama ekran hiç okumuyordu: aynı tohum
                     * iki kanalda iki ayrı sayfa veriyordu ve bu,
                     * düzeltmenin kendisinin yarısının hiç
                     * çalışmadığı anlamına geliyordu. Rota haritasında
                     * hatları ayıran tek şey de bu.
                     */
                    strokeDasharray={p.dash}
                  />
                </g>
              )
            })}
          </svg>
          {(node.labels ?? []).map((l, i) => (
            <span key={i} className="plabel" style={{ left: `${l.x * 100}%`, top: `${l.y * 100}%` }}>
              {l.text}
            </span>
          ))}
        </div>
      )
    }

    case 'scatter':
      /**
       * Serbest yerlesim.
       *
       * Kare bir alan: `height: 160` sabitken sahne genis ekranda
       * yatay bir seride donusuyor ve 0-1 arasi oranlarla dagitilmis
       * sekiller birbirine yaklasip ust uste biniyordu. Uretim
       * tarafindaki "en az 0.16 uzaklik" guvencesi ancak alan kare
       * kalirsa ekranda da geciyor.
       */
      return (
        <div
          className={'escatter' + (node.overlap ? ' ortus' : '')}
          /*
           * Örtüşen kümede alan KÂĞITTAKİYLE AYNI ölçüde.
           *
           * Ekranda serbest bırakılmıştı (420 piksele kadar) ve aynı
           * tohum iki kanalda iki ayrı zorluk veriyordu: merkez
           * aralığı oran olarak verildiği için geniş alanda şekiller
           * birbirine hiç değmiyor, görev figür-zemin olmaktan çıkıp
           * sayma görevine dönüyordu. Tablette bakınca görüldü.
           */
          style={node.overlap ? { width: node.height, height: node.height } : undefined}
        >
          {node.items.map((it, i) => (
            <span
              key={i}
              style={{
                position: 'absolute',
                left: `${it.x * 100}%`,
                top: `${it.y * 100}%`,
                transform: `translate(-50%,-50%) rotate(${it.rot ?? 0}deg)`,
                fontSize: 24,
                /*
                 * `size` 48 pikselin çarpanı — kâğıtla aynı sayı, aynı
                 * anlam. Örtüşen kümede şekil boyu ile merkez aralığı
                 * birlikte anlam taşıyor; ikisi aynı birimde olmalı.
                 */
                ...(it.size ? { width: it.size * 48, height: it.size * 48 } : {}),
              }}
            >
              <Uyaran shape={it.shape} glyph={it.glyph} />
            </span>
          ))}
        </div>
      )

    case 'rhythm':
      /**
       * Ritim dugumunu `Ritim` bileseni caliyor; burada cizilecek
       * bir sey yok. Dal yine de var: iki tuketici de her dugumu
       * tanimak zorunda.
       */
      return null

    case 'eyes':
      /**
       * Goz cizimleri.
       *
       * Durgan: dokunulan sey cizim degil, altindaki palet. Eslestirme
       * sorusunda cocuk harfi seciyor — hem kagitta hem ekranda ayni
       * soru, ayni cevap bicimi. Cizime dokundurmak ekranda daha hos
       * olurdu ama o zaman iki kanal ayni soruyu farkli yoldan
       * cevaplardi ve harf etiketleri yalnizca kagitta anlam tasirdi.
       */
      return (
        <div
          className={"egozler" + (node.cols === 1 ? " tek" : "")}
          style={{ gridTemplateColumns: `repeat(${node.cols}, 1fr)` }}
        >
          {node.items.map((it, i) => (
            <div className="egoz" key={i}>
              <Gozler cizim={it.cizim} onek={`n${sira}i${i}_`} />
              {it.etiket && <span className="gharf">{it.etiket}</span>}
            </div>
          ))}
        </div>
      )

    case 'frames':
      /**
       * Cizim kutulari YALNIZCA KAGIT.
       *
       * Sablon `channels: ['print']` ve ekranda hic acilmiyor; bu dal
       * yine de var cunku cizim modeli tek ve iki tuketicisi de her
       * dugumu tanimak zorunda. Bir gun cizim yuzeyi eklenirse yeri
       * burasi.
       */
      return (
        <p className="note">
          Bu çalışma kâğıt üzerinde yapılıyor: {node.items.length} kutu.
        </p>
      )

    case 'dots':
      /**
       * Duragan gosterim: model ve varsa arka plan deseni.
       *
       * Kopyalama/izleme alani burada cizilmiyor; onlari
       * `NoktaKopyala` ve `GizliSekil` dokunulabilir haliyle veriyor.
       * Burada da cizilse cocuk dokunmayan bir izgaraya dokunmayi
       * deniyor.
       */
      return (
        <div className="dkutu">
          {node.label && <span className="etiket">{node.label}</span>}
          <NoktaIzgara n={node.n} yol={node.model} arka={node.edges} />
        </div>
      )

    case 'key':
      return (
        <div className="ekey">
          {node.pairs.map((p, i) => (
            <div className="p" key={i}>
              <b>
                <Uyaran shape={p.fromShape} glyph={p.from} />
              </b>
              <span>{p.to}</span>
            </div>
          ))}
        </div>
      )

    case 'rows': {
      const palette = node.palette
      return (
        <>
          {/* Sabit sütun sayısı dar ekranda taşıyordu: sütunlar artık
              yere göre kendiliğinden sarıyor. */}
          <div className="erows auto">
            {node.items.map((it, i) => {
              const id = it.id ?? 'r' + i
              const active = focus === id
              return (
                <div className={'erow' + (active ? ' active' : '')} key={i}>
                  <span className="lb">
                    <Uyaran shape={it.labelShape} glyph={it.label} />
                  </span>
                  {it.blank &&
                    (palette ? (
                      <span className="slot" onClick={() => !locked && onFocus(id)}>
                        <Dizi metin={typed[id] ?? ''} bos={' '} />
                      </span>
                    ) : (
                      <input
                        inputMode="text"
                        disabled={locked}
                        value={typed[id] ?? ''}
                        onFocus={() => onFocus(id)}
                        onChange={(e) => onType(id, e.target.value)}
                      />
                    ))}
                </div>
              )
            })}
          </div>

          {palette && !locked && (
            <div className="palette">
              <div className="note" style={{ textAlign: 'left', width: '100%' }}>
                {focus ? 'Seçili satıra yazılıyor' : 'Önce bir satıra dokun'}
              </div>
              {palette.map((p) => {
                const g = kimlik(p)
                return (
                <button
                  key={g}
                  className="mini glyph-btn"
                  disabled={!focus}
                  onClick={() => {
                    if (!focus) return
                    if (node.paletteMode === 'sequence') {
                      const cur = typed[focus] ?? ''
                      onType(focus, cur ? `${cur} ${g}` : g)
                    } else {
                      // Tek değerli satır: yaz ve kendiliğinden bir sonraki
                      // boş satıra geç — her seferinde satıra dokunmak
                      // gereksiz bir zahmet
                      onType(focus, g)
                      const ids = node.items.map((it, k) => it.id ?? 'r' + k)
                      const at = ids.indexOf(focus)
                      const nextEmpty =
                        ids.slice(at + 1).find((id) => !typed[id]) ??
                        ids.find((id) => !typed[id] && id !== focus)
                      onFocus(nextEmpty ?? null)
                    }
                  }}
                >
                  <Uyaran {...p} />
                </button>
                )
              })}
              <button
                className="mini"
                disabled={!focus}
                onClick={() => {
                  if (!focus) return
                  if (node.paletteMode === 'sequence') {
                    const parts = (typed[focus] ?? '').split(' ').filter(Boolean)
                    parts.pop()
                    onType(focus, parts.join(' '))
                  } else {
                    onType(focus, '')
                  }
                }}
              >
                ⌫
              </button>
            </div>
          )}
        </>
      )
    }

    case 'choices':
      return (
        <div className="echoices">
          {node.items.map((it) => (
            <div
              key={it.id}
              className={'echoice' + (picked.has(it.id) ? ' sel' : '')}
              onClick={() => !locked && onToggle(it.id)}
            >
              <Uyaran shape={it.shape} glyph={it.glyph} />
            </div>
          ))}
        </div>
      )

    /**
     * Desen döşemeleri. Dolu hücre arka planla çiziliyor; aynı matris
     * burada ve A4'te aynı şekli veriyor çünkü arada yazı tipi yok.
     *
     * Seçili döşeme hem çerçeve hem renk alıyor: renk tek başına ipucu
     * taşımıyor (bkz. dokuman/gorsellik.md §0).
     */
    case 'tiles':
      return (
        <div className="etiles">
          {node.items.map((it) => (
            <div
              key={it.id}
              className={
                'etile' +
                (node.pick ? ' pick' : '') +
                (node.pick && picked.has(it.id) ? ' sel' : '')
              }
              onClick={() => node.pick && !locked && onToggle(it.id)}
            >
              {/* Basligi olan dosemede secenek numarasi yok: katman
                  kutusunun ustunde iki ayri sayi, cocugu sasirtiyordu. */}
              {it.caption && <span className="tcap">{it.caption}</span>}
              <div
                className="tgrid"
                style={{ gridTemplateColumns: `repeat(${node.n}, 1fr)` }}
              >
                {it.on.map((dolu, i) => (
                  <i key={i} className={dolu ? 'on' : ''} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )

    case 'scales':
      /*
       * Terazi: kirisin iki ucunda kefe, ortada esit isareti.
       *
       * Durgan — dokunulan sey terazi degil, altindaki sayi paleti.
       * Kefeye dokundurmak akla geliyor ama cevap "kac tane", yani
       * bir sayi; kefeye dokunup sayi girmek iki adim olurdu.
       */
      return (
        <div className="escales">
          {node.items.map((it, i) => (
            <div className={'eterazi' + (it.soru ? ' soru' : '')} key={i}>
              <span className="kefe">
                {it.sol.map((sk, k) => (
                  <Uyaran key={k} shape={sk} />
                ))}
              </span>
              <span className="esit">=</span>
              {it.soru ? (
                <span className="kefe soru">
                  <em>?</em>
                  {it.soruSekli && <Uyaran shape={it.soruSekli} />}
                </span>
              ) : (
                <span className="kefe">
                  {it.sag.map((sk, k) => (
                    <Uyaran key={k} shape={sk} />
                  ))}
                </span>
              )}
            </div>
          ))}
        </div>
      )
  }
}

/* ---------- nokta ızgarası: hem model hem çizim alanı ---------- */

/**
 * `n × n` nokta ve üzerine çizilmiş kırık çizgi.
 *
 * Noktalar ve çizgi AYNI SVG içinde: ayrı katmanlarda olsalardı ölçek
 * farkı yüzünden çizginin ucu noktanın yanına düşerdi ve çocuk neyi
 * kopyalayacağını bilemezdi. viewBox kare ve esnetme yok — esnetilmiş
 * bir kare ızgara artık kare değil ve kopyalama görevinin ölçtüğü şey
 * tam olarak oran.
 */
function NoktaIzgara({
  n,
  yol,
  onNokta,
  arka,
}: {
  n: number
  yol: number[]
  onNokta?: (i: number) => void
  /**
   * Arka plan deseni: sıralı olmayan kenar yığını.
   *
   * Gizli şekil görevinde desen tek bir kırık çizgi değil; çocuğun
   * çizdiği yol onun ÜZERİNDE ilerliyor. Soluk değil tam renkte
   * çiziliyor — soluk bir desen görevi kolaylaştırmaz, değiştirir:
   * aranan şekil zeminden rengiyle ayrılmaya başlar.
   */
  arka?: [number, number][]
}) {
  const adim = 1000 / (n + 1)
  const yer = (i: number): [number, number] => [
    ((i % n) + 1) * adim,
    (Math.floor(i / n) + 1) * adim,
  ]
  return (
    <svg className={'noktalar' + (onNokta ? ' dokunur' : '')} viewBox="0 0 1000 1000">
      {(arka ?? []).map(([a, b], i) => {
        const [x1, y1] = yer(a)
        const [x2, y2] = yer(b)
        return (
          <line
            key={'a' + i}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke="var(--ink)"
            strokeWidth={5}
            strokeLinecap="round"
          />
        )
      })}
      {yol.length > 1 && (
        <polyline
          points={yol
            .map((i) =>
              yer(i)
                .map((v) => v.toFixed(1))
                .join(','),
            )
            .join(' ')}
          fill="none"
          stroke="currentColor"
          strokeWidth={6}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      )}
      {Array.from({ length: n * n }, (_, i) => {
        const [x, y] = yer(i)
        const son = yol[yol.length - 1] === i
        return <circle key={i} cx={x} cy={y} r={son ? 17 : 11} fill="currentColor" />
      })}
      {onNokta
        ? Array.from({ length: n * n }, (_, i) => {
            const [x, y] = yer(i)
            return (
              /*
               * Dokunma hedefi görünen noktadan büyük: 11 piksellik bir
               * daireye parmakla isabet ettirmek çocuk için bir görev
               * değil, bir engel. Saydam daire noktanın ÜSTÜNDE duruyor.
               */
              <circle
                key={'v' + i}
                cx={x}
                cy={y}
                r={Math.min(60, adim * 0.45)}
                fill="transparent"
                onClick={() => onNokta(i)}
              />
            )
          })
        : null}
    </svg>
  )
}

/* ---------- iz: duraklara sırayla dokun ---------- */

/**
 * İz sürme.
 *
 * Çizgi dokundukça çiziliyor: kâğıtta çocuk kalemi kaldırmadan
 * ilerliyor ve ekranda da geçtiği yolu görmesi gerekiyor — yoksa nerede
 * kaldığını etiketleri yeniden okuyarak buluyor ve görev sırayı
 * tutmaktan çok sayfayı yeniden taramaya dönüyor.
 *
 * Geri alma SON DURAĞA tekrar dokunmak. Ayrı bir "geri al" düğmesi de
 * olurdu ama o düğme ekranın altında, çocuğun parmağı ızgarada: her
 * hatada gözün aşağı inip yukarı çıkması gerekiyordu.
 */
function IzSurme({
  instance,
  result,
  onResult,
}: {
  instance: ExerciseInstance
  result: GradeResult | null
  onResult: (r: GradeResult, cevap: unknown) => void
}) {
  const sc = instance.render.find((n) => n.t === 'scatter') as
    | Extract<RenderNode, { t: 'scatter' }>
    | undefined
  const [sira, setSira] = useState<string[]>([])
  const locked = result !== null
  if (!sc) return null

  const yerOf = (id: string) => sc.items.find((x) => x.id === id)!

  const dokun = (id: string) => {
    if (locked) return
    setSira((s) => {
      if (s[s.length - 1] === id) return s.slice(0, -1)
      if (s.includes(id)) return s
      return [...s, id]
    })
  }

  return (
    <>
      <div className="iz">
        <svg viewBox="0 0 100 100" preserveAspectRatio="none">
          {sira.length > 1 && (
            <polyline
              points={sira
                .map((id) => {
                  const p = yerOf(id)
                  return `${(p.x * 100).toFixed(2)},${(p.y * 100).toFixed(2)}`
                })
                .join(' ')}
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              vectorEffect="non-scaling-stroke"
            />
          )}
        </svg>
        {sc.items.map((it) => {
          const id = it.id!
          const gidildi = sira.includes(id)
          return (
            <button
              key={id}
              className={'durak' + (gidildi ? ' gidildi' : '')}
              style={{ left: `${it.x * 100}%`, top: `${it.y * 100}%` }}
              onClick={() => dokun(id)}
            >
              {it.glyph}
            </button>
          )
        })}
      </div>
      <p className="note">
        {sira.length} / {sc.items.length} durak
      </p>
      <Score result={result} />
      {!locked && (
        <div className="controls">
          <button
            className="primary wide"
            onClick={() => onResult(getExercise(instance.code).grade(instance, sira), sira)}
          >
            Kontrol et
          </button>
        </div>
      )}
    </>
  )
}

/* ---------- kopyala: modeli nokta ızgarasına çiz ---------- */

/**
 * Nokta ızgarasına kopyalama.
 *
 * Komşu olmayan noktaya atlamak ENGELLENİYOR, serbest bırakılıp
 * puanlamada cezalandırılmıyor. Gerekçe: modelde böyle bir çizgi hiç
 * yok, yani atlama bir cevap değil bir yanlış anlama. Çocuğun yanlış
 * anlamasını puana çevirmek, ölçtüğümüz şeyi değiştiriyor.
 */
function NoktaKopyala({
  instance,
  result,
  onResult,
}: {
  instance: ExerciseInstance
  result: GradeResult | null
  onResult: (r: GradeResult, cevap: unknown) => void
}) {
  const d = instance.render.find((n) => n.t === 'dots') as
    | Extract<RenderNode, { t: 'dots' }>
    | undefined
  const [yol, setYol] = useState<number[]>([])
  const [uyari, setUyari] = useState(false)
  const locked = result !== null
  if (!d) return null

  const komsu = (a: number, b: number) => {
    const dr = Math.abs(Math.floor(a / d.n) - Math.floor(b / d.n))
    const dc = Math.abs((a % d.n) - (b % d.n))
    return dr <= 1 && dc <= 1 && dr + dc > 0
  }

  const dokun = (i: number) => {
    if (locked) return
    setUyari(false)
    setYol((y) => {
      if (y.length === 0) return [i]
      if (y[y.length - 1] === i) return y.slice(0, -1)
      if (!komsu(y[y.length - 1]!, i)) {
        setUyari(true)
        return y
      }
      return [...y, i]
    })
  }

  return (
    <>
      <div className="kopya">
        <div>
          <span className="etiket">model</span>
          <NoktaIzgara n={d.n} yol={d.model} />
        </div>
        <div>
          <span className="etiket">sen çiz</span>
          <NoktaIzgara n={d.n} yol={yol} onNokta={dokun} />
        </div>
      </div>
      {uyari && <p className="note">Yan yana olmayan noktaya geçemezsin.</p>}
      <Score result={result} />
      {!locked && (
        <div className="controls">
          <button className="wide" onClick={() => setYol([])}>
            Baştan
          </button>
          <button
            className="primary wide"
            onClick={() => onResult(getExercise(instance.code).grade(instance, yol), yol)}
          >
            Kontrol et
          </button>
        </div>
      )}
    </>
  )
}

/* ---------- bak: sahneye bak, kapanınca cevapla ---------- */

/**
 * Gecikmeli görsel hatırlama.
 *
 * Sahne SÜRE İLE kapanıyor, çocuğun "bitti" demesiyle değil: bakma
 * süresi çocuğa bırakılırsa ölçümden çıkıyor ve iki çocuğun sonucu
 * karşılaştırılamaz hale geliyor. Başlatmak ise çocuğun elinde — hazır
 * olmadan başlayan bir geri sayım, belleği değil hazırlıksız
 * yakalanmayı ölçüyor.
 *
 * Sorular sahne kapandıktan SONRA görünüyor. Aynı ekranda dursalardı
 * görev gecikmeli hatırlama olmaktan çıkıp sayma görevine dönerdi;
 * kâğıt kanalının kapalı olmasının sebebi de bu.
 */
function Sahne({
  code,
  instance,
  result,
  onResult,
}: {
  code: ExerciseCode
  instance: ExerciseInstance
  result: GradeResult | null
  onResult: (r: GradeResult, cevap: unknown) => void
}) {
  const saniye = Number(instance.params['saniye'] ?? 12)
  const [faz, setFaz] = useState<'hazir' | 'goster' | 'cevapla'>('hazir')
  const [kalan, setKalan] = useState(saniye)

  useEffect(() => {
    if (faz !== 'goster') return
    if (kalan <= 0) {
      setFaz('cevapla')
      return
    }
    const t = setTimeout(() => setKalan((k) => k - 1), 1000)
    return () => clearTimeout(t)
  }, [faz, kalan])

  const sahne = instance.render.find((n) => n.t === 'scatter')
  /*
   * Sorular aşamasında sahne ağaçtan ÇIKARILIYOR. Puanlama
   * `instance.solution` üzerinden gittiği için cevap aynı kalıyor;
   * `MarkOrType` ise ağaçta ne varsa onu çiziyor.
   */
  const sorular = useMemo(
    () => ({ ...instance, render: instance.render.filter((n) => n.t !== 'scatter') }),
    [instance],
  )

  if (faz === 'hazir') {
    return (
      <div className="controls">
        <p className="note">
          Hazır olduğunda başla. Resim {saniye} saniye açık kalacak, sonra kapanacak.
        </p>
        <button className="primary wide" onClick={() => setFaz('goster')}>
          Hazırım
        </button>
      </div>
    )
  }

  if (faz === 'goster') {
    return (
      <>
        {sahne && (
          <Node
            node={sahne}
            picked={new Set()}
            typed={{}}
            focus={null}
            locked
            onToggle={() => {}}
            onType={() => {}}
            onFocus={() => {}}
          />
        )}
        <p className="note">{kalan} saniye</p>
      </>
    )
  }

  return <MarkOrType code={code} instance={sorular} mode="yaz" result={result} onResult={onResult} />
}

/* ---------- gizli: desenin üzerinden geç ---------- */

/**
 * Gizli şekil (Gottschaldt).
 *
 * Üstte aranan şekil, altta desen; çocuk desenin üzerinde noktalara
 * dokunarak şekli çiziyor.
 *
 * DOKUNUŞ YALNIZCA ÇİZİLİ KENAR ÜZERİNDE ilerliyor. Serbest bırakmak,
 * desende olmayan bir çizgiyi "cevap" olarak kabul etmek demekti;
 * çocuk o çizgiyi çizebildiği için orada bir çizgi olduğunu sanıyor ve
 * hatasını göremiyor. Engellemek, yanlış anlamayı puana çevirmekten
 * daha doğru: ölçtüğümüz şey desenin içinden şekli ayırmak, parmağın
 * nereye düştüğü değil.
 */
function GizliSekil({
  instance,
  result,
  onResult,
}: {
  instance: ExerciseInstance
  result: GradeResult | null
  onResult: (r: GradeResult, cevap: unknown) => void
}) {
  const dugumler = instance.render.filter((n) => n.t === 'dots') as Extract<
    RenderNode,
    { t: 'dots' }
  >[]
  const hedef = dugumler[0]
  const desen = dugumler.find((n) => n.trace)
  const [yol, setYol] = useState<number[]>([])
  const [uyari, setUyari] = useState(false)
  const locked = result !== null
  if (!hedef || !desen) return null

  const kenarlar = new Set(
    (desen.edges ?? []).map(([a, b]) => (a < b ? `${a}-${b}` : `${b}-${a}`)),
  )

  const dokun = (i: number) => {
    if (locked) return
    setUyari(false)
    setYol((y) => {
      if (y.length === 0) return [i]
      if (y[y.length - 1] === i) return y.slice(0, -1)
      const a = y[y.length - 1]!
      const ad = a < i ? `${a}-${i}` : `${i}-${a}`
      if (!kenarlar.has(ad)) {
        setUyari(true)
        return y
      }
      return [...y, i]
    })
  }

  return (
    <>
      <div className="gizli">
        <div className="dkutu">
          <span className="etiket">aranan şekil</span>
          <NoktaIzgara n={hedef.n} yol={hedef.model} />
        </div>
        <div className="dkutu">
          <span className="etiket">desen</span>
          <NoktaIzgara n={desen.n} yol={yol} arka={desen.edges} onNokta={dokun} />
        </div>
      </div>
      {uyari && <p className="note">Orada çizgi yok; çizili olanların üzerinden git.</p>}
      <Score result={result} />
      {!locked && (
        <div className="controls">
          <button className="wide" onClick={() => setYol([])}>
            Baştan
          </button>
          <button
            className="primary wide"
            onClick={() => onResult(getExercise(instance.code).grade(instance, yol), yol)}
          >
            Kontrol et
          </button>
        </div>
      )}
    </>
  )
}

/* ---------- kule: çubuktan çubuğa top taşı ---------- */

/**
 * Londra Kulesi.
 *
 * Üstte hedef düzen, altta çocuğun tahtası. Bir çubuğa dokunmak en
 * üstteki topu kaldırıyor, ikinci dokunuş onu bırakıyor.
 *
 * HAMLE SAYISI GÖSTERİLİYOR ama uyarı yok: "çok hamle yaptın" demek,
 * çocuğu plan yapmaktan vazgeçirip deneme yanılmaya itiyor — zaten
 * deneme yanılma yapıyorsa sayacı görmesi onu hızlandırmaz, utandırır.
 * Sayaç yalnızca kendi planını izleyebilmesi için duruyor.
 *
 * GERİ ALMA YOK. Kâğıtta da yok: hamleyi yapmadan önce düşünmek
 * görevin kendisi. "Baştan" düğmesi var çünkü tıkanan bir çocuğun
 * önünde çıkış olmalı, ama o da sayacı sıfırlıyor.
 */
function Kule({
  instance,
  result,
  onResult,
}: {
  instance: ExerciseInstance
  result: GradeResult | null
  onResult: (r: GradeResult, cevap: unknown) => void
}) {
  const sol = instance.solution as {
    bas: string[][]
    hedef: string[][]
    enAz: number
  }
  const [tahta, setTahta] = useState<string[][]>(() => sol.bas.map((y) => [...y]))
  const [elde, setElde] = useState<number | null>(null)
  const [hamleler, setHamleler] = useState<[number, number][]>([])
  const locked = result !== null
  const yukseklik = sol.bas.reduce((a, y) => a + y.length, 0)

  const dokun = (c: number) => {
    if (locked) return
    if (elde === null) {
      if (tahta[c]!.length === 0) return
      setElde(c)
      return
    }
    if (elde === c) {
      setElde(null)
      return
    }
    const yeni = tahta.map((y) => [...y])
    const top = yeni[elde]!.pop()
    if (!top) {
      setElde(null)
      return
    }
    yeni[c]!.push(top)
    setTahta(yeni)
    setHamleler([...hamleler, [elde, c]])
    setElde(null)
  }

  const basta = () => {
    setTahta(sol.bas.map((y) => [...y]))
    setHamleler([])
    setElde(null)
  }

  const tahtaCiz = (durum: string[][], dokunulur: boolean) => (
    <div className="kule">
      {durum.map((yigin, c) => (
        <button
          key={c}
          className={'cubuk' + (elde === c ? ' elde' : '')}
          onClick={dokunulur ? () => dokun(c) : undefined}
          disabled={!dokunulur}
        >
          <span className="yigin">
            {Array.from({ length: yukseklik }, (_, r) => {
              // En üst satır başta: yığın aşağıdan doluyor
              const top = yigin[yukseklik - 1 - r]
              return (
                <span className="yuva" key={r}>
                  {top ? <Uyaran shape={top as never} /> : null}
                </span>
              )
            })}
          </span>
          <span className="taban">{c + 1}</span>
        </button>
      ))}
    </div>
  )

  const bitti = tahta.map((y) => y.join(',')).join('|') === sol.hedef.map((y) => y.join(',')).join('|')

  return (
    <>
      <p className="note" style={{ textAlign: 'left' }}>
        Hedef düzen:
      </p>
      {tahtaCiz(sol.hedef, false)}
      <p className="note" style={{ textAlign: 'left' }}>
        Senin tahtan — {hamleler.length} hamle
      </p>
      {tahtaCiz(tahta, true)}
      <Score result={result} />
      {!locked && (
        <div className="controls">
          <button className="wide" onClick={basta}>
            Baştan
          </button>
          <button
            className="primary wide"
            disabled={!bitti}
            onClick={() =>
              onResult(getExercise(instance.code).grade(instance, hamleler), hamleler)
            }
          >
            {bitti ? 'Kontrol et' : 'Hedefe ulaş'}
          </button>
        </div>
      )}
    </>
  )
}

/* ---------- koridor: parmakla iz sürme ---------- */

/**
 * Dinamik iz sürme.
 *
 * Parmak A'dan başlayıp koridorun içinden B'ye gidiyor. Kâğıttaki
 * kuralın ikisi de burada geçerli: kalemi kaldırmamak ve kenarlara
 * değmemek.
 *
 * PARMAK KALKINCA YOL SIFIRLANIYOR. Kâğıtta kalemi kaldırmak görevi
 * bozuyor; ekranda kaldığı yerden devam ettirmek, kâğıttaki işi
 * ekranda daha kolay hale getirirdi ve aynı tohum iki kanalda iki
 * ayrı zorluk verirdi.
 *
 * KENARA DEĞİNCE DURDURULMUYOR. Durdurmak, hatayı o anda görünür
 * kılıp çocuğu düzeltmeye davet ederdi; ölçtüğümüz şey ise bir
 * kerede ne kadar temiz gidebildiği. Değme puanlamada görünüyor,
 * ekranda değil.
 */
function IzKoridor({
  instance,
  result,
  onResult,
}: {
  instance: ExerciseInstance
  result: GradeResult | null
  onResult: (r: GradeResult, cevap: unknown) => void
}) {
  const sol = instance.solution as {
    orta: [number, number][]
    bas: [number, number]
    son: [number, number]
  }
  const node = instance.render.find((n) => n.t === 'paths') as
    | Extract<RenderNode, { t: 'paths' }>
    | undefined
  const kutu = useRef<HTMLDivElement | null>(null)
  const [yol, setYol] = useState<[number, number][]>([])
  const [ciziyor, setCiziyor] = useState(false)
  const [uyari, setUyari] = useState<string | null>(null)
  const locked = result !== null
  if (!node) return null

  const yakin = (a: [number, number], b: [number, number], r: number) =>
    Math.hypot(a[0] - b[0], a[1] - b[1]) <= r

  /** Ekran noktasını kutunun 0–1 koordinatına çevirir. */
  const oran = (e: React.PointerEvent): [number, number] | null => {
    const kutuEl = kutu.current
    if (!kutuEl) return null
    const r = kutuEl.getBoundingClientRect()
    if (r.width === 0) return null
    return [(e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height]
  }

  const bitti = yol.length > 1 && yakin(yol[yol.length - 1]!, sol.son, 0.08)

  const basla = (e: React.PointerEvent) => {
    if (locked) return
    const nk = oran(e)
    if (!nk) return
    if (!yakin(nk, sol.bas, 0.09)) {
      setUyari("A'dan başla.")
      return
    }
    e.currentTarget.setPointerCapture(e.pointerId)
    setUyari(null)
    setCiziyor(true)
    setYol([nk])
  }

  const surukle = (e: React.PointerEvent) => {
    if (!ciziyor || locked) return
    const nk = oran(e)
    if (!nk) return
    setYol((y) => {
      const son = y[y.length - 1]
      // Çok yakın örnekleri atla: veri şişmesin
      if (son && Math.hypot(son[0] - nk[0], son[1] - nk[1]) < 0.004) return y
      return [...y, nk]
    })
  }

  const birak = () => {
    if (!ciziyor) return
    setCiziyor(false)
    setYol((y) => {
      const son = y[y.length - 1]
      if (son && yakin(son, sol.son, 0.08)) return y
      // Parmak yolda kalktı: kâğıttaki kuralla aynı, baştan
      setUyari("Parmağını kaldırdın. A'dan yeniden başla.")
      return []
    })
  }

  const cizgi = (noktalar: [number, number][]) =>
    noktalar.map(([x, y]) => `${(x * 1000).toFixed(1)},${(y * 1000).toFixed(1)}`).join(' ')

  return (
    <>
      <div className="koridor" ref={kutu}>
        <svg
          viewBox="0 0 1000 1000"
          preserveAspectRatio="xMidYMid meet"
          onPointerDown={basla}
          onPointerMove={surukle}
          onPointerUp={birak}
          onPointerCancel={birak}
        >
          {node.items.map((it, i) => (
            <polyline
              key={i}
              points={cizgi(it.points as [number, number][])}
              fill="none"
              stroke="var(--ink)"
              strokeWidth={4}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          ))}
          {yol.length > 1 && (
            <polyline
              points={cizgi(yol)}
              fill="none"
              stroke="var(--accent)"
              strokeWidth={6}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          )}
          {(node.labels ?? []).map((l, i) => (
            <text
              key={i}
              x={l.x * 1000}
              y={l.y * 1000 + 10}
              textAnchor="middle"
              fontSize={34}
              fontWeight={700}
              fill="var(--ink)"
            >
              {l.text}
            </text>
          ))}
        </svg>
      </div>
      {uyari && <p className="note">{uyari}</p>}
      <Score result={result} />
      {!locked && (
        <div className="controls">
          <button className="wide" onClick={() => { setYol([]); setUyari(null) }}>
            Baştan
          </button>
          <button
            className="primary wide"
            disabled={!bitti}
            onClick={() => onResult(getExercise(instance.code).grade(instance, yol), yol)}
          >
            {bitti ? 'Kontrol et' : "B'ye ulaş"}
          </button>
        </div>
      )}
    </>
  )
}

/* ---------- ritim: dinle, sonra aynı ritimde dokun ---------- */

/**
 * Ritmi tekrarla.
 *
 * SES WEBAUDIO İLE VE ZAMANLAMA AudioContext SAATİNDEN. `setTimeout`
 * ile çalmak akla geliyor ama tarayıcı zamanlayıcısı onlarca
 * milisaniye kayabiliyor ve bu görevde ölçtüğümüz şey tam olarak
 * milisaniye. Ses donanım saatine göre planlanıyor; ekrandaki
 * yanıp sönme `setTimeout` ile, çünkü o yalnızca bir eşlikçi.
 *
 * SES TEK BAŞINA BIRAKILMADI. Tablet sessizde olabilir ve çocuk
 * hiçbir şey duymadan "dinledim" sanabilir; ritim aynı anda bir
 * noktanın yanıp sönmesiyle de veriliyor. Aynı bilgi iki kanaldan —
 * §0.1'in sesli karşılığı.
 *
 * DOKUNMA AŞAMASINDA HAREKET YOK (§0.2). Çalarken yanıp sönen nokta,
 * tekrar aşamasında duruyor: o aşamada ölçülen şey çocuğun kendi
 * zamanlaması ve ekranda oynayan bir şey onu sürükler.
 *
 * SEKME ARKAYA DÜŞERSE DENEME GEÇERSİZ. Ölçüm bataryasındaki
 * `stalled` denetiminin küçük kardeşi: çocuk duymadığı bir ritmi
 * tekrarlamış olur ve o veri ölçüm değil gürültü.
 */
function Ritim({
  instance,
  result,
  onResult,
}: {
  instance: ExerciseInstance
  result: GradeResult | null
  onResult: (r: GradeResult, cevap: unknown) => void
}) {
  const node = instance.render.find((n) => n.t === 'rhythm') as
    | Extract<RenderNode, { t: 'rhythm' }>
    | undefined
  const [faz, setFaz] = useState<'hazir' | 'caliyor' | 'vur'>('hazir')
  const [parlak, setParlak] = useState(false)
  const [vuruslar, setVuruslar] = useState<number[]>([])
  const [uyari, setUyari] = useState<string | null>(null)
  const sesRef = useRef<AudioContext | null>(null)
  const zamanlayici = useRef<number[]>([])
  const locked = result !== null

  useEffect(
    () => () => {
      for (const z of zamanlayici.current) window.clearTimeout(z)
      void sesRef.current?.close()
    },
    [],
  )

  if (!node) return null
  const beklenen = node.araliklar.length + 1

  const cal = () => {
    if (locked) return
    setUyari(null)
    setVuruslar([])
    setFaz('caliyor')

    type SesliPencere = Window & { webkitAudioContext?: typeof AudioContext }
    const Ctx = window.AudioContext ?? (window as SesliPencere).webkitAudioContext
    const ctx = sesRef.current ?? (Ctx ? new Ctx() : null)
    sesRef.current = ctx
    void ctx?.resume()

    // Vuruş zamanları: ilk vuruş hemen, sonrakiler aralıklara göre
    const anlar = [0]
    for (const a of node.araliklar) anlar.push(anlar[anlar.length - 1]! + a)

    const bas = (ctx?.currentTime ?? 0) + 0.15
    for (const an of anlar) {
      if (ctx) {
        const osc = ctx.createOscillator()
        const kazanc = ctx.createGain()
        osc.frequency.value = 880
        kazanc.gain.value = 0.0001
        osc.connect(kazanc).connect(ctx.destination)
        const t = bas + an / 1000
        // Tık değil kısa bir bip: ani başlangıç hoparlörde çıtırdıyor
        kazanc.gain.setValueAtTime(0.0001, t)
        kazanc.gain.exponentialRampToValueAtTime(0.25, t + 0.01)
        kazanc.gain.exponentialRampToValueAtTime(0.0001, t + 0.07)
        osc.start(t)
        osc.stop(t + 0.1)
      }
      zamanlayici.current.push(
        window.setTimeout(() => {
          setParlak(true)
          window.setTimeout(() => setParlak(false), 90)
        }, 150 + an),
      )
    }

    const toplam = 150 + anlar[anlar.length - 1]! + 300
    zamanlayici.current.push(
      window.setTimeout(() => {
        if (document.hidden) {
          setUyari('Ekran kapalıydı, ritmi duymadın. Yeniden dinle.')
          setFaz('hazir')
          return
        }
        setFaz('vur')
      }, toplam),
    )
  }

  const vur = (e: React.PointerEvent) => {
    if (faz !== 'vur' || locked) return
    // `timeStamp` olay saatinden: donanım zamanı, `Date.now` değil
    const t = e.timeStamp || performance.now()
    setVuruslar((v) => (v.length >= beklenen ? v : [...v, t]))
  }

  const tamam = vuruslar.length === beklenen

  return (
    <>
      <div className={'ritim' + (parlak ? ' parlak' : '')} onPointerDown={vur}>
        {faz === 'vur' ? (
          <span className="sayac">
            {vuruslar.length} / {beklenen}
          </span>
        ) : (
          <span className="nokta" />
        )}
      </div>
      <p className="note">
        {faz === 'hazir' && 'Önce dinle.'}
        {faz === 'caliyor' && 'Dinliyorsun…'}
        {faz === 'vur' && 'Şimdi aynı ritimde kutuya dokun.'}
      </p>
      {uyari && <p className="note">{uyari}</p>}
      <Score result={result} />
      {!locked && (
        <div className="controls">
          <button className="wide" onClick={cal} disabled={faz === 'caliyor'}>
            {faz === 'hazir' ? 'Dinle' : 'Yeniden dinle'}
          </button>
          <button
            className="primary wide"
            disabled={!tamam}
            onClick={() => {
              // Mutlak zaman değil ARALIKLAR gönderiliyor: ilk vuruş sıfır
              const ilk = vuruslar[0]!
              const zamanlar = vuruslar.map((t) => Math.round(t - ilk))
              onResult(getExercise(instance.code).grade(instance, zamanlar), zamanlar)
            }}
          >
            {tamam ? 'Kontrol et' : `${beklenen} kez dokun`}
          </button>
        </div>
      )}
    </>
  )
}
